import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PanelFrame from "../../ui/PanelFrame";
import siteConfig from "../../../config/siteConfig";
import guideSpecialContent from "../../../config/guideSpecialContent";
import { useLang } from "../../../i18n/LangContext";
import { guidesHe } from "../../../i18n/translations";
import guidesParagraphsHe from "../../../i18n/guidesParagraphsHe";
import "./Guides.scss";

function guideCatTitle(cat, isHe) {
  if (!isHe) return cat.title;
  return guidesHe[cat.id]?.title || cat.title;
}

function guideCatSubtitle(cat, isHe) {
  if (!isHe) return cat.subtitle;
  return guidesHe[cat.id]?.subtitle || cat.subtitle;
}

function guideTopicTitle(topic, isHe) {
  if (!isHe) return topic.title;
  return guidesHe[topic.id] || topic.title;
}

function guideParagraphs(topic, isHe) {
  if (!isHe) return topic.paragraphs || [];
  return guidesParagraphsHe[topic.id] || topic.paragraphs || [];
}

function pickLang(value, isHe) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return isHe ? value.he || value.en || "" : value.en || value.he || "";
}

function GuideImg({ src, alt, variant }) {
  if (!src) {
    return (
      <div
        className={`mu-guides__img-fallback${
          variant ? ` mu-guides__img-fallback--${variant}` : ""
        }`}
        aria-hidden
      />
    );
  }
  const cls = ["mu-guides__thumb"];
  if (variant) cls.push(`mu-guides__thumb--${variant}`);
  return (
    <img
      className={cls.join(" ")}
      src={src}
      alt={alt || ""}
      loading="lazy"
      onError={(e) => {
        e.currentTarget.style.display = "none";
        const fb = e.currentTarget.nextElementSibling;
        if (fb) fb.hidden = false;
      }}
    />
  );
}

function ItemCardView({ item, isHe, variant }) {
  if (!item) return null;
  const name = pickLang(item.name, isHe);
  const note = pickLang(item.note, isHe);
  const kind = variant || item.variant || "item";
  return (
    <div className={`mu-guides__itemcard mu-guides__itemcard--${kind}`}>
      <div className="mu-guides__itemcard-art">
        <GuideImg src={item.img} alt={name} variant={kind} />
        <div className="mu-guides__img-fallback" hidden>
          {name.slice(0, 2)}
        </div>
      </div>
      <div className="mu-guides__itemcard-body">
        <strong>{name}</strong>
        {item.qty ? <span className="mu-guides__chip">{item.qty}</span> : null}
        {item.level ? (
          <span className="mu-guides__chip">{item.level}</span>
        ) : null}
        {item.option ? (
          <span className="mu-guides__chip">
            {isHe ? "אופציה" : "Option"} {item.option}
          </span>
        ) : null}
        {item.excellent ? (
          <span className="mu-guides__chip is-exc">
            {isHe ? "Excellent" : "Excellent"}
          </span>
        ) : null}
        {item.ancient ? (
          <span className="mu-guides__chip is-anc">
            {isHe ? "Ancient" : "Ancient"}
          </span>
        ) : null}
        {note ? <p>{note}</p> : null}
      </div>
    </div>
  );
}

function GuideBlocks({ blocks, isHe }) {
  if (!Array.isArray(blocks) || !blocks.length) return null;

  return (
    <div className="mu-guides__blocks">
      {blocks.map((block, idx) => {
        const key = `${block.type}-${idx}`;

        if (block.type === "p" || block.type === "note" || block.type === "warn") {
          return (
            <p
              key={key}
              className={
                block.type === "note"
                  ? "mu-guides__note"
                  : block.type === "warn"
                  ? "mu-guides__warn"
                  : undefined
              }
            >
              {isHe ? block.he : block.en}
            </p>
          );
        }

        if (block.type === "h") {
          return (
            <h3 key={key} className="mu-guides__heading">
              {isHe ? block.he : block.en}
            </h3>
          );
        }

        if (block.type === "classevo") {
          const stages = block.stages || [];
          return (
            <div key={key} className="mu-guides__classevo" dir={isHe ? "rtl" : "ltr"}>
              {stages.map((stage, si) => (
                <div key={si} className="mu-guides__classevo-row">
                  <figure className="mu-guides__classevo-stage">
                    <GuideImg
                      src={stage.img}
                      alt={pickLang(stage.name, isHe)}
                      variant="character"
                    />
                    <figcaption>
                      {stage.label ? (
                        <span className="mu-guides__classevo-label">
                          {pickLang(stage.label, isHe)}
                        </span>
                      ) : null}
                      <strong>{pickLang(stage.name, isHe)}</strong>
                    </figcaption>
                  </figure>
                  {si < stages.length - 1 ? (
                    <span className="mu-guides__classevo-arrow" aria-hidden>
                      {isHe ? "←" : "→"}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          );
        }

        if (block.type === "npc") {
          const name = pickLang(block.name, isHe);
          return (
            <article key={key} className="mu-guides__npc">
              <div className="mu-guides__npc-art">
                <GuideImg src={block.img} alt={name} variant="npc" />
                <div className="mu-guides__img-fallback" hidden>
                  NPC
                </div>
              </div>
              <div>
                <h4>{name}</h4>
                <p>
                  <span>{isHe ? "מפה" : "Map"}:</span>{" "}
                  {pickLang(block.map, isHe)}
                  {block.coords ? (
                    <>
                      {" · "}
                      <span>{isHe ? "קואורדינטות" : "Coords"}:</span>{" "}
                      {block.coords}
                    </>
                  ) : null}
                </p>
                {block.note ? <p>{pickLang(block.note, isHe)}</p> : null}
              </div>
            </article>
          );
        }

        if (block.type === "itemcard") {
          return (
            <ItemCardView
              key={key}
              item={block}
              isHe={isHe}
              variant={block.variant || "item"}
            />
          );
        }

        if (block.type === "mix") {
          const ingredients = block.ingredients || [];
          const results = block.results
            ? block.results
            : block.result
            ? [block.result]
            : [];
          return (
            <article key={key} className="mu-guides__mix">
              <header className="mu-guides__mix-head">
                <h4>{pickLang(block.title, isHe)}</h4>
              </header>
              <div className="mu-guides__mix-flow">
                <div className="mu-guides__mix-ings">
                  {ingredients.map((ing, ii) => (
                    <div key={ii} className="mu-guides__mix-slot">
                      <ItemCardView item={ing} isHe={isHe} />
                      {ii < ingredients.length - 1 ? (
                        <span className="mu-guides__mix-plus" aria-hidden>
                          +
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
                <div className="mu-guides__mix-arrow" aria-hidden>
                  ↓
                </div>
                <div className="mu-guides__mix-machine">
                  {isHe ? "Chaos Machine" : "Chaos Machine"}
                </div>
                <div className="mu-guides__mix-arrow" aria-hidden>
                  ↓
                </div>
                <div className="mu-guides__mix-results">
                  {results.map((r, ri) => (
                    <ItemCardView
                      key={ri}
                      item={r}
                      isHe={isHe}
                      variant={r.variant || "result"}
                    />
                  ))}
                </div>
              </div>
              <ul className="mu-guides__mix-meta">
                {block.zen ? (
                  <li>
                    <span>{isHe ? "עלות Zen" : "Zen cost"}</span>
                    <strong>{pickLang(block.zen, isHe)}</strong>
                  </li>
                ) : null}
                {block.rate ? (
                  <li>
                    <span>{isHe ? "הצלחה" : "Success"}</span>
                    <strong>{pickLang(block.rate, isHe)}</strong>
                  </li>
                ) : null}
                {block.fail ? (
                  <li>
                    <span>{isHe ? "בכישלון" : "On failure"}</span>
                    <strong>{pickLang(block.fail, isHe)}</strong>
                  </li>
                ) : null}
              </ul>
            </article>
          );
        }

        if (block.type === "timeline") {
          return (
            <ol key={key} className="mu-guides__timeline">
              {(block.steps || []).map((step, si) => (
                <li key={si}>
                  {step.img ? (
                    <GuideImg src={step.img} alt="" />
                  ) : (
                    <span className="mu-guides__timeline-num">{si + 1}</span>
                  )}
                  <div>
                    <strong>{pickLang(step.title, isHe)}</strong>
                    <p>{pickLang(step.body, isHe)}</p>
                  </div>
                </li>
              ))}
            </ol>
          );
        }

        if (block.type === "results") {
          return (
            <div key={key} className="mu-guides__results">
              {block.title ? (
                <h4>{pickLang(block.title, isHe)}</h4>
              ) : null}
              <div className="mu-guides__results-grid">
                {(block.items || []).map((it, ii) => (
                  <div key={ii} className="mu-guides__result-card">
                    <GuideImg src={it.img} alt={pickLang(it.name, isHe)} />
                    <strong>{pickLang(it.name, isHe)}</strong>
                    {it.classes ? (
                      <span>{pickLang(it.classes, isHe)}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if (block.type === "recipe") {
          const name = pickLang(block.name, isHe);
          const dungeon = pickLang(block.dungeon, isHe);
          const rates = isHe ? block.ratesHe || block.rates : block.rates;
          return (
            <article key={key} className="mu-guides__recipe">
              <header className="mu-guides__recipe-head">
                <h4>{name}</h4>
                <span className="mu-guides__recipe-tag">{dungeon}</span>
              </header>
              <p className="mu-guides__recipe-formula">
                {(block.materials || [])
                  .map((m) => pickLang(m, isHe))
                  .join(" + ")}
                {" → "}
                <strong>{block.result}</strong>
              </p>
              <ul className="mu-guides__recipe-meta">
                <li>
                  <span>{isHe ? "תוצאה" : "Result"}</span>
                  <strong>{block.result}</strong>
                </li>
                <li>
                  <span>{isHe ? "עלות Zen" : "Zen cost"}</span>
                  <strong>{Number(block.zen).toLocaleString("en-US")}</strong>
                </li>
                <li>
                  <span>{isHe ? "הצלחה (VIP)" : "Success (VIP)"}</span>
                  <strong>{rates}</strong>
                </li>
                <li>
                  <span>{isHe ? "בכישלון" : "On failure"}</span>
                  <strong>
                    {isHe
                      ? "כל החומרים אובדים"
                      : "All materials are lost"}
                  </strong>
                </li>
              </ul>
              <div className="mu-guides__recipe-mats">
                <strong>{isHe ? "חומרים נדרשים" : "Required materials"}</strong>
                <ul>
                  {(block.materials || []).map((m, mi) => (
                    <li key={mi}>{pickLang(m, isHe)}</li>
                  ))}
                </ul>
              </div>
            </article>
          );
        }

        if (block.type === "table") {
          return (
            <div key={key} className="mu-guides__table-wrap">
              <table className="mu-guides__table">
                <thead>
                  <tr>
                    {(block.columns || []).map((col) => (
                      <th key={col.key}>{isHe ? col.he : col.en}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(block.rows || []).map((row, ri) => (
                    <tr key={ri}>
                      {(block.columns || []).map((col) => (
                        <td key={col.key}>{pickLang(row[col.key], isHe)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

const Guides = () => {
  const { t, isHe } = useLang();
  const categories = siteConfig.guides || [];
  const [searchParams] = useSearchParams();

  const firstTopic = useMemo(() => {
    const cat = categories[0];
    const topic = cat?.topics?.[0];
    if (!cat || !topic) return null;
    return { categoryId: cat.id, topicId: topic.id };
  }, [categories]);

  const deepLink = useMemo(() => {
    const catId = searchParams.get("cat");
    const topicId = searchParams.get("topic");
    if (!catId || !topicId) return null;
    const cat = categories.find((c) => c.id === catId);
    const topic = cat?.topics?.find((t) => t.id === topicId);
    if (!cat || !topic) return null;
    return { categoryId: cat.id, topicId: topic.id };
  }, [categories, searchParams]);

  const [openCategoryId, setOpenCategoryId] = useState(
    deepLink?.categoryId || firstTopic?.categoryId || null
  );
  const [active, setActive] = useState(deepLink || firstTopic);

  useEffect(() => {
    if (deepLink) {
      setActive(deepLink);
      setOpenCategoryId(deepLink.categoryId);
      return;
    }
    if (!active && firstTopic) {
      setActive(firstTopic);
      setOpenCategoryId(firstTopic.categoryId);
    }
  }, [deepLink, active, firstTopic]);

  const activeCategory = categories.find((c) => c.id === active?.categoryId);
  const activeTopic = activeCategory?.topics?.find(
    (topic) => topic.id === active?.topicId
  );
  const specialBlocks = activeTopic
    ? guideSpecialContent[activeTopic.id]
    : null;

  const toggleCategory = (id) => {
    setOpenCategoryId((prev) => (prev === id ? null : id));
  };

  const selectTopic = (categoryId, topicId) => {
    setOpenCategoryId(categoryId);
    setActive({ categoryId, topicId });
  };

  return (
    <div className="mu-guides" dir={isHe ? "rtl" : "ltr"}>
      <div className="mu-guides__toolbar">
        <Link className="mu-guides__back" to="/">
          {t("back_to_home")}
        </Link>
      </div>

      <div className="mu-guides__layout">
        <aside className="mu-guides__nav">
          <PanelFrame title={t("guides")} className="mu-guides__nav-frame">
            <ul className="mu-guides__categories">
              {categories.map((cat) => {
                const isOpen = openCategoryId === cat.id;
                return (
                  <li key={cat.id} className="mu-guides__category">
                    <button
                      type="button"
                      className={`mu-guides__cat-btn${
                        isOpen ? " is-open" : ""
                      }${
                        active?.categoryId === cat.id ? " is-active-cat" : ""
                      }`}
                      onClick={() => toggleCategory(cat.id)}
                      aria-expanded={isOpen}
                    >
                      <span className="mu-guides__cat-text">
                        <strong>{guideCatTitle(cat, isHe)}</strong>
                        <span>{guideCatSubtitle(cat, isHe)}</span>
                      </span>
                      <span className="mu-guides__chevron" aria-hidden>
                        {isOpen ? "▾" : "▸"}
                      </span>
                    </button>

                    {isOpen ? (
                      <ul className="mu-guides__topics">
                        {(cat.topics || []).map((topic) => (
                          <li key={topic.id}>
                            <button
                              type="button"
                              className={`mu-guides__topic-btn${
                                active?.topicId === topic.id &&
                                active?.categoryId === cat.id
                                  ? " is-active"
                                  : ""
                              }`}
                              onClick={() => selectTopic(cat.id, topic.id)}
                            >
                              {guideTopicTitle(topic, isHe)}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </PanelFrame>
        </aside>

        <section className="mu-guides__content">
          <PanelFrame
            title={
              activeTopic
                ? guideTopicTitle(activeTopic, isHe)
                : t("select_a_guide")
            }
            className="mu-guides__content-frame"
          >
            {activeTopic ? (
              <div className="mu-guides__article">
                <p className="mu-guides__breadcrumb">
                  {guideCatTitle(activeCategory, isHe)}
                  {" / "}
                  {guideTopicTitle(activeTopic, isHe)}
                </p>
                {specialBlocks ? (
                  <GuideBlocks blocks={specialBlocks} isHe={isHe} />
                ) : (
                  (guideParagraphs(activeTopic, isHe) || []).map((p) => (
                    <p key={p}>{p}</p>
                  ))
                )}
                {Array.isArray(activeTopic.items) && activeTopic.items.length ? (
                  <div className="mu-guides__items">
                    {activeTopic.items.map((item) => (
                      <figure key={item.label} className="mu-guides__item">
                        <img src={item.src} alt={item.label} />
                        <figcaption>{item.label}</figcaption>
                      </figure>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="mu-guides__empty">{t("choose_guide_topic")}</p>
            )}
          </PanelFrame>
        </section>
      </div>
    </div>
  );
};

export default Guides;
